import { CheckCircleIcon, WarningIcon } from "@chakra-ui/icons";
import { Avatar, Box } from "@chakra-ui/react";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { UserRead } from "@nens/rana/api-v1";
import { RanaHeading, RanaTable } from "@nens/ranalib";
import { SchematisationRevision } from "@nens/threedi/openapi-client";

import { BackButton } from "@/components/back-button";
import { Profile } from "@/lib/profile";
import { useHccRevisionsListQuery } from "@/queries/query-hcc-schematisation-revision";
import { useTenantUserListQuery } from "@/queries/query-tenant-user";

export interface SchematisationRevisionHistoryProps {
  schematisationId: number;
  backLabel: ReactNode;
  back: () => void;
  setSelectedRevision: (revisionId: number) => void;
}

type RevisionWithAuthor = SchematisationRevision & { author: string };

function formatIsValid(isValid: boolean | null | undefined) {
  if (isValid == null) return "-";
  if (isValid) return <CheckCircleIcon color="semantic.success.main" boxSize={4} />;
  return <WarningIcon color="semantic.error.main" boxSize={4} />;
}

function getAuthorName(rev: SchematisationRevision): string {
  return (
    [rev.commitFirstName, rev.commitLastName].filter(Boolean).join(" ") || rev.commitUser || ""
  );
}

export function SchematisationRevisionHistory({
  setSelectedRevision,
  schematisationId,
  backLabel,
  back,
}: SchematisationRevisionHistoryProps) {
  const intl = useIntl();
  const { data: revisions, isLoading } = useHccRevisionsListQuery(schematisationId);
  const { data: tenantUsers } = useTenantUserListQuery({ limit: 100 });

  const [searchString, setSearchString] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const userByEmail = useMemo(
    () =>
      new Map<string, UserRead>(
        (tenantUsers?.items ?? [])
          .filter((u): u is UserRead & { email: string } => !!u.email)
          .map((u) => [u.email, u])
      ),
    [tenantUsers]
  );

  const revisionsWithAuthor: RevisionWithAuthor[] = useMemo(
    () =>
      (revisions?.results ?? []).map((rev) => ({
        ...rev,
        author: getAuthorName(rev),
      })),
    [revisions]
  );

  const filteredRevisions = useMemo(
    () =>
      revisionsWithAuthor.filter((rev) => {
        if (!searchString) return true;
        const search = searchString.toLowerCase();
        return (
          rev.author.toLowerCase().includes(search) ||
          (rev.commitMessage ?? "").toLowerCase().includes(search) ||
          String(rev.number).includes(search)
        );
      }),
    [revisionsWithAuthor, searchString]
  );

  const numPages = Math.ceil(filteredRevisions.length / pageSize);
  const paginatedRevisions = filteredRevisions.slice((page - 1) * pageSize, page * pageSize);

  const selectRevision = useCallback(
    (revisionId: number) => {
      setSelectedRevision(revisionId);
      back();
    },
    [back, setSelectedRevision]
  );

  const renderAuthor = useCallback(
    (rev: RevisionWithAuthor) => {
      const matchedUser = rev.commitUser ? userByEmail.get(rev.commitUser) : undefined;
      if (matchedUser) {
        return <Profile inTable user={matchedUser} />;
      }
      return (
        <Avatar
          size="sm"
          name={rev.author || undefined}
          bg="brand.glacier"
          color="brand.analytic"
        />
      );
    },
    [userByEmail]
  );

  return (
    <Box mb={2}>
      <Box gap={2} alignItems="center">
        <BackButton back={back}>{backLabel}</BackButton>
        <RanaHeading type="h3" my={2}>
          <FormattedMessage id="button.revision_history" defaultMessage="Revision History" />
        </RanaHeading>
      </Box>
      <RanaTable
        columnDefinitions={[
          { title: "#", width: "table_cell_xxs" },
          {
            title: intl.formatMessage({ id: "revision.author", defaultMessage: "Author" }),
            width: "table_cell_xxs",
          },
          {
            title: intl.formatMessage({
              id: "revision.commit_message",
              defaultMessage: "Commit message",
            }),
            width: "table_cell",
          },
          {
            title: intl.formatMessage({ id: "revision.created", defaultMessage: "Created" }),
            width: "table_cell_xs",
          },
          {
            title: intl.formatMessage({ id: "revision.valid", defaultMessage: "Valid" }),
            width: "table_cell_xxs",
          },
          {
            title: intl.formatMessage({ id: "revision.model", defaultMessage: "HCC model" }),
            width: "table_cell_xxs",
          },
        ]}
        tableDataIds={paginatedRevisions.map((rev) => String(rev.id))}
        isSkeleton={isLoading}
        emptyElement={
          <RanaTable.Empty>
            <FormattedMessage id="revision.no_results" defaultMessage="No revisions found" />
          </RanaTable.Empty>
        }
      >
        <RanaTable.Filters
          search={{
            placeholder: intl.formatMessage({
              id: "revision.search_placeholder",
              defaultMessage: "Search revision",
            }),
            searchString,
            setSearchString: (value: string) => {
              setSearchString(value);
              setPage(1);
            },
          }}
        />
        <RanaTable.Container>
          <RanaTable.Header />
          <RanaTable.Body>
            {paginatedRevisions.map((rev) => {
              const { id } = rev;
              return (
                <RanaTable.Row
                  key={id}
                  rowId={String(id)}
                  onClick={id !== undefined ? () => selectRevision(id) : undefined}
                >
                  <RanaTable.Cell>{rev.number}</RanaTable.Cell>
                  <RanaTable.Cell>{renderAuthor(rev)}</RanaTable.Cell>
                  <RanaTable.Cell>{rev.commitMessage}</RanaTable.Cell>
                  <RanaTable.Cell>{rev.commitDate?.toLocaleDateString()}</RanaTable.Cell>
                  <RanaTable.Cell>{formatIsValid(rev.isValid)}</RanaTable.Cell>
                  <RanaTable.Cell>{formatIsValid(rev.hasThreedimodel)}</RanaTable.Cell>
                </RanaTable.Row>
              );
            })}
          </RanaTable.Body>
        </RanaTable.Container>
        <RanaTable.Pagination
          limit={pageSize}
          setPageSize={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          page={page}
          numPages={numPages}
          setPage={setPage}
        />
      </RanaTable>
    </Box>
  );
}
